'use client';

import Slider, { Settings } from 'react-slick';
import { useTheme, Box, Stack } from '@mui/material';
import { ImageWithFallback } from '@qb/frontend/components/ImageWithFallback';
import { ReactSlickStyle } from '@qb/frontend/components/ReactSlickStyle/ReactSlickStyle';
import { BaseCarouselArrow } from '@/components/Common/BaseCarousel/BaseCarouselArrow';
import { PartPhotoDialog } from '@/global/pages/PartDisplayPage/_components/PartPhotoDialog';
import { TenantConfig } from '@/shared/types/controllers/sps/SPSAppControllerTypes';
import { PartDisplayDataResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';
type PartPhotoGalleryProps = Pick<PartDisplayDataResponse, 'part'> & {
  tenantConfig: TenantConfig;
  part: PartDisplayDataResponse['part'];
  images: PartDisplayDataResponse['part']['images'];
};
export const PartPhotoGallery = ({
  part,
  images,
  tenantConfig
}: PartPhotoGalleryProps) => {
  const theme = useTheme();
  const hasGallery = !!images?.length && images?.length > 1;
  if (!hasGallery) {
    return <PartPhotoDialog image={images[0]} part={part} tenantConfig={tenantConfig} />;
  }
  const carouselSettings: Settings = {
    customPaging: i => {
      const image = images.at(i);
      if (image) {
        return <Stack component="span" sx={{
          alignItems: 'center',
          justifyContent: 'center'
        }}>
            <ImageWithFallback tenantAlias={tenantConfig.tenantAlias} src={image.target} alt={part.partNumber} title={part.partNumber} priority width={70} height={70} />
          </Stack>;
      }
      return <div />;
    },
    dots: true,
    dotsClass: 'slick-dots slick-thumb',
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: theme.transitions.easing.easeInOut,
    prevArrow: <BaseCarouselArrow isSmallSize variant="prev" />,
    nextArrow: <BaseCarouselArrow isSmallSize variant="next" />
  };
  return <>
      <ReactSlickStyle data-sentry-element="ReactSlickStyle" data-sentry-source-file="PartPhotoGallery.tsx" />
      <Box sx={{
      px: 0,
      '& .slick-slider': {},
      '& .slick-list': {},
      '& .slick-dots': {},
      '& .slick-dots li': {},
      '& .slick-dots li button': {},
      '& .slick-dots li button:before': {},
      '& .slick-dots li.slick-active button:before': {},
      '& .slick-thumb': {
        bottom: 0,
        m: 0,
        mt: 1,
        position: 'relative',
        display: 'grid!important',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridGap: 8
      },
      '& .slick-thumb li': {
        m: 0,
        p: 0,
        width: '100%',
        height: 72,
        border: '1px solid transparent',
        borderColor: 'common.backgroundColor'
      },
      '& .slick-thumb li.slick-active': {
        borderColor: 'primary.light'
      },
      '& .slick-thumb li img': {
        opacity: 0.7
      },
      '& .slick-thumb li.slick-active img': {
        opacity: 1
      }
    }} data-sentry-element="Box" data-sentry-source-file="PartPhotoGallery.tsx">
        <Slider {...carouselSettings} data-sentry-element="Slider" data-sentry-source-file="PartPhotoGallery.tsx">
          {images.map(image => <div key={image.id}>
              <PartPhotoDialog image={image} part={part} tenantConfig={tenantConfig} />
            </div>)}
        </Slider>
      </Box>
    </>;
};